import { loggerNamespace } from '../lib/Logger'
import { rpc } from '../lib/Rpc'
import { store } from '../store'
import { authService } from './AuthService'
import { IProfileGetProfile } from '../interfaces/rpc/IProfileGetProfile'
import { IBusinessGetProfileById } from '../interfaces/rpc/IBusinessGetProfileById'
import { IFieldGetFieldsList } from '../interfaces/rpc/IFieldGetFieldsList'
import { IFieldGetFieldGroups } from '../interfaces/rpc/IFieldGetFieldGroups'
import { IFieldGetFieldPresets } from '../interfaces/rpc/IFieldGetFieldPresets'
import { IResourceGetBusinessResources } from '../interfaces/rpc/IResourceGetBusinessResources'
import { FieldsCache } from '../interfaces/FieldsCache'

type IResource = IResourceGetBusinessResources[number]

// ProfileService orchestrates global profile state:
// - active business + resource pair (businessId/resourceId)
// - business metadata cache (`businessesById`) used for UI labels and timezone lookups
// - per-business field metadata cache (`fieldsByBusinessId`) with `fieldGroups`, `fieldPresets`, `fieldsList`
//
// Invariants:
// - active resource selection always implies an active business (setResource triggers business switch)
// - setting businessId drives both business profile loading and field cache hydration for that business
// - `profile.business` represents the active business, while `businessesById` keeps lookups for all loaded businesses
// - field cache entries are only applied to `fieldGroups/fieldPresets/fieldsList` when their business matches `businessId`
class ProfileService {
  private logger = loggerNamespace('ProfileService')

  private async fetchBusinessProfile(businessId: string) {
    if (!businessId) return null
    const { error, result } = await rpc.call<IBusinessGetProfileById>('business.get_profile_by_id', {
      business: { id: businessId },
      skip_worker_sorting: true,
    })
    this.logger.info('business.get_profile_by_id', { error, result })
    return result || null
  }

  private updateBusinessEntry(businessId: string, payload: { name?: string, timezone?: string }) {
    if (!businessId) return
    const existing = store.profile.businessesById[businessId]
    const businessName = payload.name || existing?.name || store.profile.profile?.businessData?.[businessId]?.name || ''
    const timezone = payload.timezone ?? existing?.timezone
    store.profile.businessesById = {
      ...store.profile.businessesById,
      [businessId]: { id: businessId, name: businessName, timezone }
    }
  }

  private async loadBusiness(businessId: string) {
    if (!businessId) return
    const result = await this.fetchBusinessProfile(businessId)
    if (result) {
      store.profile.business = result
      this.updateBusinessEntry(businessId, {
        name: result.business.general_info?.name || result.business.general_info?.shortName,
        timezone: result.business.general_info?.timezone
      })
    }
  }

  private updateActiveFields(businessId: string, cache: FieldsCache) {
    if (businessId !== store.profile.businessId) return
    // field cache only applied when businessId matches the current active business
    store.profile.fieldGroups = cache.fieldGroups || null
    store.profile.fieldPresets = cache.fieldPresets || null
    store.profile.fieldsList = cache.fieldsList || null
  }

  private async loadFieldsForBusiness(businessId: string): Promise<FieldsCache> {
    if (!businessId) return {}
    const existing = store.profile.fieldsByBusinessId[businessId] || {}
    const cache: FieldsCache = {
      fieldGroups: existing.fieldGroups,
      fieldPresets: existing.fieldPresets,
      fieldsList: existing.fieldsList
    }

    // Sequentially load field metadata to avoid hammering the RPC backend and keep cache consistency.
    {
      const { error, result } = await rpc.call<IFieldGetFieldGroups>('field.get_field_groups', {
        business: { id: businessId }
      })
      this.logger.info('field.get_field_groups', { error, result })
      if (result) {
        cache.fieldGroups = result
      }
    }

    {
      const { error, result } = await rpc.call<IFieldGetFieldPresets>('field.get_field_presets', {
        business: { id: businessId }
      })
      this.logger.info('field.get_field_presets', { error, result })
      if (result) {
        cache.fieldPresets = result
      }
    }

    {
      const { error, result } = await rpc.call<IFieldGetFieldsList>('field.get_fields_list', {
        business: { list: [businessId] },
        field: { active: true }
      })
      this.logger.info('field.get_fields_list', { error, result })
      if (result) {
        cache.fieldsList = result.find((d: any) => d.id == businessId)?.value || []
      }
    }

    store.profile.fieldsByBusinessId = {
      ...store.profile.fieldsByBusinessId,
      [businessId]: cache
    }
    this.updateActiveFields(businessId, cache)
    return cache
  }

  private initializeBusinessEntries(profile: IProfileGetProfile) {
    const newMap = { ...store.profile.businessesById }
    Object.entries(profile.businessData || {}).forEach(([businessId, data]) => {
      newMap[businessId] = {
        id: businessId,
        name: data.name,
        timezone: newMap[businessId]?.timezone,
      }
    })
    store.profile.businessesById = newMap
  }

  // NOTE: loadProfile orchestrates the initial profile/business/resource bootstrap.
  // It loads the profile, records business metadata, activates the first business, warms caches, and selects the default resource.
  public loadProfile = async () => {
    const { error, result } = await rpc.call<IProfileGetProfile>('profile.get_profile', {
      id: authService.authData.userInfo?.user_id,
      with_business_info: true,
    })
    this.logger.info('profile.get_profile', { error, result })
    if (result) {
      store.profile.profile = result
      this.initializeBusinessEntries(result)
      const businessId = Object.keys(result.businessData)[0]
      if (businessId) {
        await this.setBusinessId(businessId)
    }
    await this.loadResources()
    this.setSelectedResource('all')
  }
  }

  // NOTE: setBusinessId triggers the explicit business context switch and keeps the global UI loading flag in sync.
  public setBusinessId = async (businessId: string) => {
    if (!businessId) return
    store.ui.loading = true // switching business can be slow; keep UI spinner active during fetch
    try {
      store.profile.businessId = businessId
      await this.loadBusiness(businessId)
      await this.loadFieldsForBusiness(businessId)
    } finally {
      store.ui.loading = false
    }
  }

  public setSelectedResource = (selected: 'all' | string) => {
    store.profile.selectedResourceId = selected
  }

  // NOTE: setResource ensures the matching business is loaded before activating the resource.
  // It drives the invariant that every resource selection requires a business context switch.
  public setResource = async (resource?: IResource | null, businessId?: string) => {
    if (!resource) return
    store.profile.selectedResourceId = resource.id
    const resolvedBusinessId = businessId || store.profile.businessIdByResourceId[resource.id]
    if (!resolvedBusinessId) return
    await this.setBusinessId(resolvedBusinessId)
    store.profile.resource = resource
  }

  // NOTE: ensureBusinessContext is a lazy loader for business metadata/fields used when we operate outside the active business.
  public ensureBusinessContext = async (businessId: string) => {
    if (!businessId) return
    if (!store.profile.businessesById[businessId]?.timezone) {
      const business = await this.fetchBusinessProfile(businessId)
      if (business) {
        this.updateBusinessEntry(businessId, {
          name: business.business.general_info?.name || business.business.general_info?.shortName,
          timezone: business.business.general_info?.timezone
        })
      }
    }
    if (!store.profile.fieldsByBusinessId[businessId]) {
      await this.loadFieldsForBusiness(businessId)
    }
  }

  // NOTE: loadResources rebuilds the resource cache for every business that the profile can access.
  // It repopulates `resources`, `resourcesByBusinessId`, and `businessIdByResourceId` from RPC results with explicit filtering.
  public loadResources = async () => {
    if (!store.profile.profile) return
    store.profile.resources = []
    store.profile.businessIdByResourceId = {}
    store.profile.resourcesByBusinessId = {}
    for (const { businessId, resourceIds } of (store.profile.profile.businessResourceIds || [])) {
      store.profile.resourcesByBusinessId[businessId] = []
      const { error, result } = await rpc.call<IResourceGetBusinessResources>('resource.get_business_resources', {
        business: { id: businessId }
      })
      this.logger.info('resource.get_business_resources', { error, result })
      if (result) {
        for (const item of result.filter((r: IResource) => resourceIds.includes(r.id))) {
          store.profile.resources.push(item)
          store.profile.businessIdByResourceId[item.id] = businessId
          store.profile.resourcesByBusinessId[businessId].push(item)
        }
      }
    }
  }
}

const profileService = new ProfileService()
export { profileService }
